/**
 * Attaching images and videos to the chat.
 *
 * The file is uploaded over HTTP and its address is then sent as an ordinary
 * message. Nothing about the chat protocol or the stored message changes, which
 * is what lets history replay, message withdrawal and plugins carry on working
 * without knowing attachments exist.
 */

/** What the message box accepts, mirroring what the server will store. */
export const ACCEPTED_TYPES = 'image/jpeg,image/png,image/gif,image/webp,image/avif,video/mp4,video/webm,video/quicktime';

/** Paths served by this app's own upload store. */
const UPLOAD_PREFIX = '/uploads/';
/** The extensions the server stores, and how each should be shown. */
const PREVIEWABLE = {
  jpg: 'image',
  png: 'image',
  gif: 'image',
  webp: 'image',
  avif: 'image',
  mp4: 'video',
  webm: 'video',
  mov: 'video',
};

/**
 * How a link should be previewed: 'image', 'video', or null for no preview.
 *
 * Only this app's own uploads are ever previewed. A link someone typed to a
 * picture elsewhere stays a link, because loading it would tell that server who
 * is in the room, when, and from which address - a tracking pixel by invitation.
 * `origin` is the page's own origin.
 */
export function previewKind(href, origin) {
  if (!href) return null;
  let url;
  try {
    url = new URL(href, origin || undefined);
  } catch {
    return null;
  }
  if (origin && url.origin !== origin) return null;
  if (!url.pathname.startsWith(UPLOAD_PREFIX)) return null;
  const name = url.pathname.slice(UPLOAD_PREFIX.length);
  // One path segment, so nothing can climb out of the upload store.
  if (!name || name.includes('/')) return null;
  const extension = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1).toLowerCase() : '';
  return PREVIEWABLE[extension] ?? null;
}

/** A short, human description of a size in bytes. */
export function describeBytes(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value < 0) return '';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} kB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Uploads one file and resolves with what the server stored, including the
 * address to send to the room.
 *
 * A ticket is fetched over the WebSocket first: uploads go over HTTP, where an
 * anonymous guest has nothing to authenticate with, so the socket - which already
 * knows who they are - vouches for the request instead.
 *
 * `onProgress` receives a fraction between 0 and 1 where the browser can report
 * it. XMLHttpRequest is used rather than fetch for exactly that reason: fetch
 * cannot report upload progress.
 */
export async function uploadFile({ connection, room, file, onProgress }) {
  const { ticket, maxBytes } = await connection.request('upload.ticket', { room });
  if (maxBytes && file.size > maxBytes) {
    throw Object.assign(new Error('That file is too large.'), {
      code: 'upload_too_large',
      params: { maxBytes },
    });
  }

  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', '/api/upload');
    request.setRequestHeader('X-Upload-Ticket', ticket);
    request.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
    });

    request.addEventListener('load', () => {
      let payload = null;
      try {
        payload = JSON.parse(request.responseText);
      } catch {
        /* handled below */
      }
      if (request.status === 201 && payload?.url) {
        resolve(payload);
        return;
      }
      reject(
        Object.assign(new Error(payload?.message ?? 'The upload failed.'), {
          code: payload?.code ?? 'upload_failed',
          params: payload?.details?.params,
        }),
      );
    });

    request.addEventListener('error', () =>
      reject(Object.assign(new Error('The upload failed.'), { code: 'upload_failed' })),
    );
    request.addEventListener('abort', () =>
      reject(Object.assign(new Error('The upload was cancelled.'), { code: 'upload_aborted' })),
    );

    request.send(file);
  });
}

export default { uploadFile, previewKind, describeBytes, ACCEPTED_TYPES };
